import type {
  Board, PlacedEmitter, InventoryEmitter, Pos, Direction, SignalMap, ClueStatus,
} from "../types.ts";
import { posKey, posEq } from "../types.ts";
import { boardRows, boardCols, getCell } from "../engine/grid.ts";
import { getArms, computeSignalMap, evaluateClues } from "../engine/signal.ts";
import { COLORS } from "./colors.ts";

interface Layout {
  cellSize: number;
  offsetX: number;
  offsetY: number;
  boardWidth: number;
  boardHeight: number;
  rows: number;
  cols: number;
  inventoryY: number;
  inventorySlotSize: number;
}

export function computeLayout(
  board: Board,
  inventoryCount: number,
  canvasW: number,
  canvasH: number
): Layout {
  const rows = boardRows(board);
  const cols = boardCols(board);

  // Reserve space for inventory at bottom
  const inventoryHeight = inventoryCount > 0 ? 80 : 0;
  const availH = canvasH - inventoryHeight - 10;
  const availW = canvasW;

  const cellSize = Math.floor(Math.min(availW / cols, availH / rows));
  const boardWidth = cellSize * cols;
  const boardHeight = cellSize * rows;
  const offsetX = Math.floor((canvasW - boardWidth) / 2);
  const offsetY = Math.floor((availH - boardHeight) / 2);

  return {
    cellSize, offsetX, offsetY, boardWidth, boardHeight,
    rows, cols,
    inventoryY: offsetY + boardHeight + 10,
    inventorySlotSize: Math.min(cellSize, 60),
  };
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  board: Board,
  placedEmitters: PlacedEmitter[],
  inventory: InventoryEmitter[],
  heldEmitterId: string | null,
  clueStatuses: Map<string, ClueStatus> | null,
  canvasW: number,
  canvasH: number
): void {
  const layout = computeLayout(board, inventory.length, canvasW, canvasH);
  const { cellSize, offsetX, offsetY, rows, cols } = layout;

  ctx.clearRect(0, 0, canvasW, canvasH);

  const emitterPosMap = new Map<string, PlacedEmitter>();
  for (const e of placedEmitters) {
    emitterPosMap.set(posKey(e.pos), e);
  }

  // Draw cells
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = offsetX + c * cellSize;
      const y = offsetY + r * cellSize;
      const cell = getCell(board, { row: r, col: c });
      if (!cell) continue;

      // Cell background
      if (cell.kind === "wall") {
        ctx.fillStyle = COLORS.cellWall;
        ctx.fillRect(x, y, cellSize, cellSize);
        // Wall cross-hatch
        ctx.strokeStyle = COLORS.wallPattern;
        ctx.lineWidth = 1;
        for (let i = 0; i < cellSize; i += 8) {
          ctx.beginPath();
          ctx.moveTo(x + i, y);
          ctx.lineTo(x, y + i);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x + cellSize - i, y + cellSize);
          ctx.lineTo(x + cellSize, y + cellSize - i);
          ctx.stroke();
        }
      } else if (cell.kind === "clue") {
        ctx.fillStyle = COLORS.cellClue;
        ctx.fillRect(x, y, cellSize, cellSize);
      } else {
        ctx.fillStyle = COLORS.cellEmpty;
        ctx.fillRect(x, y, cellSize, cellSize);
      }

      // Clue number + status color
      if (cell.kind === "clue" && cell.clueValue !== undefined) {
        const pk = posKey({ row: r, col: c });
        const status = clueStatuses?.get(pk);
        if (status === "satisfied") {
          ctx.fillStyle = COLORS.clueSatisfied;
        } else if (status === "exceeded") {
          ctx.fillStyle = COLORS.clueExceeded;
        } else {
          ctx.fillStyle = COLORS.clueUnsatisfied;
        }
        ctx.font = `bold ${Math.floor(cellSize * 0.5)}px monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          String(cell.clueValue),
          x + cellSize / 2,
          y + cellSize / 2
        );
      }

      // Emitter on this cell
      const emitter = emitterPosMap.get(posKey({ row: r, col: c }));
      if (emitter) {
        drawEmitter(ctx, emitter, x, y, cellSize, emitter.fixed);
      }
    }
  }

  // Draw beams (faint lines showing signal paths)
  drawBeams(ctx, board, placedEmitters, layout);

  // Grid lines
  ctx.strokeStyle = COLORS.gridLine;
  ctx.lineWidth = 1;
  for (let r = 0; r <= rows; r++) {
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY + r * cellSize);
    ctx.lineTo(offsetX + cols * cellSize, offsetY + r * cellSize);
    ctx.stroke();
  }
  for (let c = 0; c <= cols; c++) {
    ctx.beginPath();
    ctx.moveTo(offsetX + c * cellSize, offsetY);
    ctx.lineTo(offsetX + c * cellSize, offsetY + rows * cellSize);
    ctx.stroke();
  }

  // Inventory
  drawInventory(ctx, inventory, heldEmitterId, layout, canvasW);
}

function drawEmitter(
  ctx: CanvasRenderingContext2D,
  emitter: PlacedEmitter,
  x: number,
  y: number,
  cellSize: number,
  isFixed: boolean
): void {
  const cx = x + cellSize / 2;
  const cy = y + cellSize / 2;
  const r = cellSize * 0.35;

  // Circle
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = isFixed ? COLORS.emitterFixed : COLORS.emitterPlayer;
  ctx.fill();

  // Border
  ctx.strokeStyle = isFixed ? "#155e75" : "#0891b2";
  ctx.lineWidth = 2;
  ctx.stroke();

  // Arms (arrows)
  const arms = getArms(emitter.type.shape, emitter.rotation);
  const arrowLen = cellSize * 0.25;

  ctx.strokeStyle = COLORS.emitterArrow;
  ctx.lineWidth = 2;

  for (const dir of arms) {
    const angle = dirToAngle(dir);
    const ax = cx + Math.cos(angle) * r * 0.5;
    const ay = cy + Math.sin(angle) * r * 0.5;
    const bx = cx + Math.cos(angle) * (r + arrowLen * 0.3);
    const by = cy + Math.sin(angle) * (r + arrowLen * 0.3);

    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();

    // Arrowhead
    const headLen = 4;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(
      bx - headLen * Math.cos(angle - 0.5),
      by - headLen * Math.sin(angle - 0.5)
    );
    ctx.moveTo(bx, by);
    ctx.lineTo(
      bx - headLen * Math.cos(angle + 0.5),
      by - headLen * Math.sin(angle + 0.5)
    );
    ctx.stroke();
  }

  // Range indicator
  if (emitter.type.range > 1) {
    ctx.fillStyle = COLORS.rangeText;
    ctx.font = `bold ${Math.floor(cellSize * 0.2)}px monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(emitter.type.range), cx, cy);
  }
}

function drawBeams(
  ctx: CanvasRenderingContext2D,
  board: Board,
  emitters: PlacedEmitter[],
  layout: Layout
): void {
  const { cellSize, offsetX, offsetY } = layout;
  const posSet = new Set(emitters.map((e) => posKey(e.pos)));

  ctx.strokeStyle = COLORS.beam;
  ctx.lineWidth = 2;

  for (const emitter of emitters) {
    const arms = getArms(emitter.type.shape, emitter.rotation);
    const ecx = offsetX + emitter.pos.col * cellSize + cellSize / 2;
    const ecy = offsetY + emitter.pos.row * cellSize + cellSize / 2;

    for (const dir of arms) {
      const { dx, dy } = dirToDelta(dir);
      let cur = emitter.pos;
      let endPos = emitter.pos;

      for (let i = 0; i < emitter.type.range; i++) {
        const next: Pos = { row: cur.row + dy, col: cur.col + dx };
        if (next.row < 0 || next.row >= layout.rows || next.col < 0 || next.col >= layout.cols) break;
        const cell = getCell(board, next);
        if (!cell || cell.kind === "wall") break;
        if (posSet.has(posKey(next))) break;
        endPos = next;
        cur = next;
      }

      if (!posEq(endPos, emitter.pos)) {
        const tx = offsetX + endPos.col * cellSize + cellSize / 2;
        const ty = offsetY + endPos.row * cellSize + cellSize / 2;
        ctx.beginPath();
        ctx.moveTo(ecx, ecy);
        ctx.lineTo(tx, ty);
        ctx.stroke();
      }
    }
  }
}

function drawInventory(
  ctx: CanvasRenderingContext2D,
  inventory: InventoryEmitter[],
  heldEmitterId: string | null,
  layout: Layout,
  canvasW: number
): void {
  if (inventory.length === 0) return;

  const { inventoryY, inventorySlotSize } = layout;
  const totalW = inventory.length * (inventorySlotSize + 8) - 8;
  const startX = Math.floor((canvasW - totalW) / 2);

  for (let i = 0; i < inventory.length; i++) {
    const item = inventory[i]!;
    const sx = startX + i * (inventorySlotSize + 8);
    const sy = inventoryY;

    // Slot background
    const isHeld = item.id === heldEmitterId;
    ctx.fillStyle = COLORS.inventoryBg;
    ctx.fillRect(sx, sy, inventorySlotSize, inventorySlotSize);
    ctx.strokeStyle = isHeld ? COLORS.inventorySelected : COLORS.inventoryBorder;
    ctx.lineWidth = isHeld ? 3 : 1;
    ctx.strokeRect(sx, sy, inventorySlotSize, inventorySlotSize);

    // Draw as a fake PlacedEmitter for rendering
    const fakeEmitter: PlacedEmitter = {
      id: item.id,
      type: item.type,
      pos: { row: 0, col: 0 },
      rotation: item.rotation,
      fixed: false,
    };
    drawEmitter(ctx, fakeEmitter, sx, sy, inventorySlotSize, false);
  }
}

function dirToAngle(dir: Direction): number {
  switch (dir) {
    case "up": return -Math.PI / 2;
    case "right": return 0;
    case "down": return Math.PI / 2;
    case "left": return Math.PI;
  }
}

function dirToDelta(dir: Direction): { dx: number; dy: number } {
  switch (dir) {
    case "up": return { dx: 0, dy: -1 };
    case "down": return { dx: 0, dy: 1 };
    case "left": return { dx: -1, dy: 0 };
    case "right": return { dx: 1, dy: 0 };
  }
}

// ── Hit testing ──

export function hitTestBoard(
  board: Board,
  inventoryCount: number,
  canvasW: number,
  canvasH: number,
  x: number,
  y: number
): { type: "board"; pos: Pos } | { type: "inventory"; index: number } | null {
  const layout = computeLayout(board, inventoryCount, canvasW, canvasH);
  const { cellSize, offsetX, offsetY, rows, cols, inventoryY, inventorySlotSize } = layout;

  // Board hit
  const col = Math.floor((x - offsetX) / cellSize);
  const row = Math.floor((y - offsetY) / cellSize);
  if (row >= 0 && row < rows && col >= 0 && col < cols) {
    return { type: "board", pos: { row, col } };
  }

  // Inventory hit
  if (y >= inventoryY && y < inventoryY + inventorySlotSize) {
    const totalW = inventoryCount * (inventorySlotSize + 8) - 8;
    const startX = Math.floor((canvasW - totalW) / 2);
    const relX = x - startX;
    if (relX >= 0) {
      const idx = Math.floor(relX / (inventorySlotSize + 8));
      const withinSlot = relX - idx * (inventorySlotSize + 8);
      if (idx >= 0 && idx < inventoryCount && withinSlot <= inventorySlotSize) {
        return { type: "inventory", index: idx };
      }
    }
  }

  return null;
}
